import { CheckCircle, Error, Help, RadioButtonUnchecked, Star } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import { useContext, useMemo } from 'react';

import { LocaleContext } from '@/Contexts/LocaleContext';
import { StandardQuestionDetails } from '@/models/Standard';
import { trackBusinessEvent } from '@/utils/telemetry';

export type StandardIconVariation = 'error' | 'comment' | 'complete' | 'neutral' | 'star awarded';

function resolveVariation(
  variation: StandardIconVariation | undefined,
  hasErrors: boolean,
  hasComments: boolean,
  hasStarAwarded: boolean
): StandardIconVariation {
  if (variation != null) return variation;
  if (hasErrors) return 'error';
  if (hasComments) return 'comment';
  if (hasStarAwarded) return 'star awarded';
  return 'complete';
}

function StandardStatusIcon({
  standard,
  loading,
  currentlySelected,
  hasComments,
  hasErrors,
  hasStarAwarded,
  variation,
  onClick,
}: {
  standard: StandardQuestionDetails;
  loading: boolean;
  currentlySelected: boolean;
  hasComments: boolean;
  hasErrors: boolean;
  hasStarAwarded: boolean;
  variation?: StandardIconVariation;
  onClick: () => void;
}) {
  const theme = useTheme();
  const { translatedText } = useContext(LocaleContext);

  const currentVariation = useMemo(
    () => resolveVariation(variation, hasErrors, hasComments, hasStarAwarded),
    [variation, hasErrors, hasComments, hasStarAwarded]
  );

  // Add new visual states by extending this map.
  const variationConfig = useMemo(
    () => ({
      error: {
        Icon: Error,
        color: theme.palette.warning.main,
        title: translatedText.applicationHeader.missingRequiredQuestions,
      },
      comment: {
        Icon: Help,
        color: theme.palette.desert.main,
        title: translatedText.applicationHeader.reviewerComments,
      },
      complete: {
        Icon: CheckCircle,
        color: theme.palette.forestTint1.main,
        title: translatedText.applicationHeader.standardCompleted,
      },
      neutral: {
        Icon: RadioButtonUnchecked,
        color: theme.palette.sandShade4.main,
        title: translatedText.applicationHeader.standardCompleted,
      },
      'star awarded': {
        Icon: Star,
        color: theme.palette.desertTint.main,
        title: translatedText.applicationHeader.starAwarded,
      },
    }),
    [
      theme.palette.warning.main,
      theme.palette.desert.main,
      theme.palette.forestTint1.main,
      theme.palette.sandShade4.main,
      theme.palette.desertTint.main,
      translatedText.applicationHeader.missingRequiredQuestions,
      translatedText.applicationHeader.reviewerComments,
      translatedText.applicationHeader.standardCompleted,
      translatedText.applicationHeader.starAwarded,
    ]
  );

  const { Icon, color: iconColor, title } = variationConfig[currentVariation];

  const iconSx = useMemo(() => {
    return {
      fill: theme.palette.background.default,
      stroke: iconColor,
      height: '1.25em',
      width: '1.25em',
      strokeWidth: '4px',
      strokeLinejoin: 'round',
      paintOrder: 'stroke',
      '&:hover': {
        // on hover grow the icon
        transform: 'scale(1.5)',
        transition: 'transform 0.2s',
      },
    } as const;
  }, [iconColor, theme.palette.background.default]);

  const selectedIconSx = useMemo(() => {
    return {
      ...iconSx,
      fill: iconColor,
      stroke: theme.palette.background.default,
      height: '1.5em',
      width: '1.5em',
    };
  }, [iconSx, iconColor, theme.palette.background.default]);

  const iconWrapperSx = useMemo(() => {
    return currentlySelected
      ? {}
      : {
          opacity: 1,
          fillOpacity: 1,
          color: theme.palette.background.default,
        };
  }, [currentlySelected, theme.palette.background.default]);

  if (standard?.standardName == null || standard.standardName.trim() === '') {
    trackBusinessEvent('StandardNameMissing', {
      standardId: standard.standardId,
      result: 'failure',
    });
    return <Box />;
  }

  if (loading) {
    return <CircularProgress size={30} sx={{ zIndex: 99999 }} />;
  }

  return (
    <Box display='flex' flexDirection='column' justifyContent='center' alignItems='center'>
      <IconButton onClick={currentlySelected ? undefined : onClick} sx={iconWrapperSx}>
        <Tooltip placement='top' title={title}>
          <Icon sx={currentlySelected ? selectedIconSx : iconSx} />
        </Tooltip>
      </IconButton>

      <Typography>{standard.standardName.split(/[-::]/)[0]}</Typography>
    </Box>
  );
}

export default StandardStatusIcon;
